import { faSave } from "@fortawesome/free-solid-svg-icons";
import { useCallback, useState } from "react";
import { useEffect } from "react";
import { getNotificationItemInfo } from "../helpers/Converters";
import { useDispatch, useSelector } from "react-redux";
import {
  selectNotifications,
  clearNotifications,
} from "../reducers/tournamentSlice";
import {
  selectNotifications as selectOrderNotifications,
  clearNotifications as clearOrderNotifications,
} from "../reducers/registrationOrderSlice";
import {
  selectUtilityNotifications,
  clearUtilityNotifications,
} from "../reducers/utilitySlice";
import {
  selectInvoiceNotifications,
  clearNotifications as clearInvoiceNotifications,
} from "../reducers/invoiceSlice";
import {
  selectNotifications as selectAppNotifications,
  clearNotifications as clearAppNotifications,
} from "../reducers/appSlice";
import { useGolfstatusNavigate } from "./navigationHooks";

export const useNotifications = (cleanUp = true) => {
  const tournamentNotifications = useSelector(selectNotifications);
  const orderNotifications = useSelector(selectOrderNotifications);
  const utilityNotifications = useSelector(selectUtilityNotifications);
  const invoiceNotifications = useSelector(selectInvoiceNotifications);
  const appNotifications = useSelector(selectAppNotifications);

  const dispatch = useDispatch();

  const allNotifications = [
    ...tournamentNotifications,
    ...orderNotifications,
    ...utilityNotifications,
    ...invoiceNotifications,
    ...appNotifications,
  ];

  const filteredNotifications = (ids = []) => {
    if (ids.length > 0) {
      return allNotifications?.filter?.((n) => ids.some((id) => n.id === id));
    }
    return allNotifications;
  };

  const clearAllNotifications = useCallback((excludeAnnouncements) => {
    dispatch(clearNotifications());
    dispatch(clearOrderNotifications());
    dispatch(clearUtilityNotifications());
    dispatch(clearInvoiceNotifications());
    dispatch(clearAppNotifications({ excludeAnnouncements: excludeAnnouncements}));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      if (cleanUp) {
        clearAllNotifications(true);
      }
    };
  }, [clearAllNotifications, cleanUp]);

  return {
    allNotifications,
    filteredNotifications,
    tournamentNotifications,
    orderNotifications,
    utilityNotifications,
    invoiceNotifications,
    appNotifications,
    clearAllNotifications,
  };
};

export const useNotificationNavigation = (
  notificationType,
  notifications,
  url,
  id
) => {
  const navigate = useGolfstatusNavigate();
  useEffect(() => {
    if (
      notifications?.length === 1 &&
      notifications?.[0].result === notificationType &&
      notifications?.[0]?.id === (id ?? notifications?.[0]?.id)
    ) {
      const nav = url?.replace?.("[id]", notifications?.[0]?.resultPayload?.id);
      navigate(nav ?? url, { replace: true });
    }
  }, [notifications, navigate, url, id, notificationType]);
};

export const useNotificationAction = (
  notificationType,
  notifications,
  callback,
  id
) => {
 
  const firstNotification = notifications?.find?.(n => n.id === (id ?? n.id)) ?? {};
  const result = firstNotification?.result ?? "";
  const notificationID = firstNotification?.id ?? "";
  const hasNotification = !!firstNotification 
  
  useEffect(() => {
    if (
      result === notificationType &&
      notificationID === (id ?? notificationID) &&
      hasNotification
    ) {
      callback?.();
    }
  }, [
    result,
    notificationID,
    callback,
    id,
    notificationType,
    hasNotification
  ]);
};

export const useNotificationBanner = (settings) => {
  const [unsaved, setUnsaved] = useState(false);
  let banner = false;

  if (settings === undefined) {
    return [banner, setUnsaved];
  }

  //currently available settings
  const {
    notifications,
    bannerActions,
    timeoutAction,
    defaultBanner,
    navigationActions,
  } = settings;
  const hasNotifications = notifications?.length > 0;

  if (defaultBanner) {
    banner = defaultBanner;
  }

  if (unsaved) {
    banner = {
      title: getNotificationItemInfo(`There are unsaved changes!`, faSave),
      state: "grey",
      bannerActions: bannerActions?.filter?.((ba) => ba.result === "unsaved"),
      type: "unsaved",
    };
  }

  const getActions = (notification, bannerActions) => {
    let actions = [];

    if (notification?.bannerActions) {
      actions.push(...notification.bannerActions);
    }
    if (bannerActions) {
      actions.push(
        ...bannerActions?.filter?.(
          (ba) => (ba.result ?? notification.result) === notification.result
        )
      );
    }

    return actions;
  };

  if (hasNotifications) {
    banner = {
      notifications: notifications?.map?.((n) => ({
        ...n,
        pageActions: getActions(n, bannerActions),
      })),
      navigationActions,
      timeoutAction: timeoutAction,
      timeout: notifications?.[0].timeout,
    };
  }

  return [banner, setUnsaved, unsaved];
};

export const getSaveBannerActions = (save, goBack) => {
  return [
    {
      id: 0,
      result: "saved",
      title: "back",
      type: "secondary",
      actionClick: goBack,
    },
    {
      id: 1,
      result: "created",
      title: "back",
      type: "secondary",
      actionClick: goBack,
    },
    {
      id: 2,
      result: "unsaved",
      title: "save",
      type: "secondary black",
      actionClick: save,
    },
    {
      id: 3,
      result: "unsaved",
      title: "discard",
      type: "secondary grey",
      actionClick: goBack,
    },
  ];
};
